import React, { useCallback, useEffect, useRef, useState } from 'react';
import Crossword, {
  CrosswordImperative,
  CrosswordGrid,
  CrosswordProps,
  CrosswordProvider,
  CrosswordProviderImperative,
  CrosswordProviderProps,
  DirectionClues,
} from '@jaredreisinger/react-crossword';
import styled from 'styled-components';

const data = {
  across: {
    1: {
      clue: 'Quand deux équipes s\'affrontent au handball, elles font un...',
      answer: 'MATCH',
      row: 0,
      col: 3,
    },
    3: {
      clue: 'Quand on donne la balle à un autre joueur dans le jeu.',
      answer: 'PASSE',
      row: 4,
      col: 4,
    },
    5: {
      clue: 'L\'objet rond qu\'on lance pour jouer au handball.',
      answer: 'BALLON',
      row: 6,
      col: 0,
    },
    6: {
      clue: 'Quand le ballon entre dans le filet et que ton équipe marque un point.',
      answer: 'BUT',
      row: 10,
      col: 5,
    },
  },
  down: {
    2: {
      clue: 'L\'endroit où deux équipes s\'affrontent au handball.',
      answer: 'TERRAIN',
      row: 0,
      col: 5,
    },
    4: {
      clue: 'Un petit objet dans lequel l\'arbitre souffle pour arrêter le jeu.',
      answer: 'SIFFLET',
      row: 4,
      col: 7,
    },
    7: {
      clue: 'Une partie de ton corps que tu utilises pour tenir le ballon.',
      answer: 'MAIN',
      row: 5,
      col: 1,
    },
  },
};


const Page = styled.div`
  padding: 0;
`;

const CrosswordMessageBlock = styled.div`

  margin: 2em 0 4em;
  display: none;
  gap: 2em;
  max-height: 20em;
`;

const CrosswordWrapper = styled.div`

  /* and some fun making use of the defined class names */
  .crossword.correct {
    rect {
      stroke: rgb(100, 200, 100) !important;
    }
    svg > rect {
      fill: rgb(100, 200, 100) !important;
    }
    text {
      fill: rgb(100, 200, 100) !important;
    }
  }

  .clue-cell {
    rect {
      stroke-width: 0.5%;
      stroke-linecap: square;
      stroke-linejoin: miter;
    }
  }

  .clue {
    font-size: 400;
    font-weight: 800;
    background-color: #14264e;
    color: #fff;
    padding: .75rem 1rem;
    border-radius: 1rem;
    margin: .25rem 0;
    max-width: 20rem;
    transition: all .5s ease-in-out;
  }
  .clue:focus {
    background-color: #fc0;
  }
  .clue.correct {
    ::before {
      content: '\u2713'; /* a.k.a. checkmark: ✓ */
      display: inline-block;
      text-decoration: none;
      color: rgb(100, 200, 100);
      margin-right: 0.25em;
    }
  }
`;

const CrosswordProviderWrapper = styled(CrosswordWrapper)`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: center;
  width: auto;
  max-width: 992px;
  margin: 0 auto;
  gap: 1em;

  .column {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    padding: 1rem;
    width: calc(50% - 2rem);

    @media screen and (max-width: 880px) {
        width: 100%;
    }
  }
  .direction {

    .header {
      margin-top: 0;
      color: #14264e;
      margin: .5rem 0 .25rem;
      font-size: 1.2rem;
      text-align: center;
    }
  }

  .grid {
    flex: 1;
    width: calc(50% - 2rem);
    padding: 2rem 1rem;
    align-content: center;

    @media screen and (max-width: 880px) {
      justify-content: center;
      width: 100%;
    }
  }
`;

const Messages = styled.pre`
  display: block;
  background-color: rgb(230, 230, 230);
  margin: 0;
  padding: 1em;
  overflow: auto;
`;

// in order to make this a more-comprehensive example, and to vet Crossword's
// features, we actually implement a fair amount...
function App() {
  const crossword = useRef<CrosswordImperative>(null);

  const focus = useCallback<React.MouseEventHandler>((event) => {
    crossword.current?.focus();
  }, []);

  const fillOneCell = useCallback<React.MouseEventHandler>((event) => {
    crossword.current?.setGuess(0, 2, 'O');
  }, []);

  const fillAllAnswers = useCallback<React.MouseEventHandler>((event) => {
    crossword.current?.fillAllAnswers();
  }, []);

  const reset = useCallback<React.MouseEventHandler>((event) => {
    crossword.current?.reset();
  }, []);

  // We don't really *do* anything with callbacks from the Crossword component,
  // but we can at least show that they are happening.  You would want to do
  // something more interesting than simply collecting them as messages.
  const messagesRef = useRef<HTMLPreElement>(null);
  const [messages, setMessages] = useState<string[]>([]);

  const clearMessages = useCallback<React.MouseEventHandler>((event) => {
    setMessages([]);
  }, []);

  const addMessage = useCallback((message: string) => {
    setMessages((m) => m.concat(`${message}\n`));
  }, []);

  useEffect(() => {
    if (!messagesRef.current) {
      return;
    }
    const { scrollHeight } = messagesRef.current;
    messagesRef.current.scrollTo(0, scrollHeight);
  }, [messages]);

  useEffect(() => {
    setTimeout(() => {
      crosswordProvider.current?.setGuess(0, 3, 'M');
      crosswordProvider.current?.setGuess(0, 5, 'T');
      crosswordProvider.current?.setGuess(4, 4, 'P');
      crosswordProvider.current?.setGuess(4, 7, 'S');
      crosswordProvider.current?.setGuess(6, 0, 'B');
      crosswordProvider.current?.setGuess(10, 5, 'B');
      crosswordProvider.current?.setGuess(5, 1, 'M');
    }, 1000);
  }, []);

  // onCorrect is called with the direction, number, and the correct answer.
  const onCorrect = useCallback<Required<CrosswordProps>['onCorrect']>(
    (direction, number, answer) => {
      addMessage(`onCorrect: "${direction}", "${number}", "${answer}"`);
    },
    [addMessage]
  );

  // onLoadedCorrect is called with an array of the already-correct answers,
  // each element itself is an array with the same values as in onCorrect: the
  // direction, number, and the correct answer.
  const onLoadedCorrect = useCallback<
    Required<CrosswordProps>['onLoadedCorrect']
  >(
    (answers) => {
      addMessage(
        `onLoadedCorrect:\n${answers
          .map(
            ([direction, number, answer]) =>
              `    - "${direction}", "${number}", "${answer}"`
          )
          .join('\n')}`
      );
    },
    [addMessage]
  );

  // onCrosswordCorrect is called with a truthy/falsy value.
  const onCrosswordCorrect = useCallback<
    Required<CrosswordProps>['onCrosswordCorrect']
  >(
    (isCorrect) => {
      addMessage(`onCrosswordCorrect: ${JSON.stringify(isCorrect)}`);
    },
    [addMessage]
  );

  // onCellChange is called with the row, column, and character.
  const onCellChange = useCallback<Required<CrosswordProps>['onCellChange']>(
    (row, col, char) => {
      addMessage(`onCellChange: "${row}", "${col}", "${char}"`);
    },
    [addMessage]
  );

  // all the same functionality, but for the decomposed CrosswordProvider
  const crosswordProvider = useRef<CrosswordProviderImperative>(null);

  const focusProvider = useCallback<React.MouseEventHandler>((event) => {
    crosswordProvider.current?.focus();
  }, []);

  const fillOneCellProvider = useCallback<React.MouseEventHandler>((event) => {
    crosswordProvider.current?.setGuess(0, 3, 'M');
  }, []);

  const fillAllAnswersProvider = useCallback<React.MouseEventHandler>(
    (event) => {
      crosswordProvider.current?.fillAllAnswers();
    },
    []
  );

  const resetProvider = useCallback<React.MouseEventHandler>((event) => {
    crosswordProvider.current?.reset();
  }, []);

  // We don't really *do* anything with callbacks from the Crossword component,
  // but we can at least show that they are happening.  You would want to do
  // something more interesting than simply collecting them as messages.
  const messagesProviderRef = useRef<HTMLPreElement>(null);
  const [messagesProvider, setMessagesProvider] = useState<string[]>([]);

  const clearMessagesProvider = useCallback<React.MouseEventHandler>(
    (event) => {
      setMessagesProvider([]);
    },
    []
  );

  const addMessageProvider = useCallback((message: string) => {
    setMessagesProvider((m) => m.concat(`${message}\n`));
  }, []);

  useEffect(() => {
    if (!messagesProviderRef.current) {
      return;
    }
    const { scrollHeight } = messagesProviderRef.current;
    messagesProviderRef.current.scrollTo(0, scrollHeight);
  }, [messagesProvider]);

  // onCorrect is called with the direction, number, and the correct answer.
  const onCorrectProvider = useCallback<
    Required<CrosswordProviderProps>['onCorrect']
  >(
    (direction, number, answer) => {
      addMessageProvider(`onCorrect: "${direction}", "${number}", "${answer}"`);
    },
    [addMessageProvider]
  );

  // onLoadedCorrect is called with an array of the already-correct answers,
  // each element itself is an array with the same values as in onCorrect: the
  // direction, number, and the correct answer.
  const onLoadedCorrectProvider = useCallback<
    Required<CrosswordProviderProps>['onLoadedCorrect']
  >(
    (answers) => {
      addMessageProvider(
        `onLoadedCorrect:\n${answers
          .map(
            ([direction, number, answer]) =>
              `    - "${direction}", "${number}", "${answer}"`
          )
          .join('\n')}`
      );
    },
    [addMessageProvider]
  );

  // onCrosswordCorrect is called with a truthy/falsy value.
  const onCrosswordCorrectProvider = useCallback<
    Required<CrosswordProviderProps>['onCrosswordCorrect']
  >(
    (isCorrect) => {
      addMessageProvider(`onCrosswordCorrect: ${JSON.stringify(isCorrect)}`);
      if(isCorrect){window.parent.document.getElementById("btn-tdm-motscroises-win").click();}
    },
    [addMessageProvider]
  );

  // onCellChange is called with the row, column, and character.
  const onCellChangeProvider = useCallback<
    Required<CrosswordProviderProps>['onCellChange']
  >(
    (row, col, char) => {
      addMessageProvider(`onCellChange: "${row}", "${col}", "${char}"`);
    },
    [addMessageProvider]
  );



  return (
    <Page>
      <CrosswordProviderWrapper>
        <CrosswordProvider 
          theme={{
            columnBreakpoint: '768px',
            gridBackground: 'transparent',
            cellBackground: '#ffffff',
            cellBorder: '#000000',
            textColor: '#95c11f',
            focusBackground: '#fef9d0',
            highlight: true,
            highlightBackground: '#fef9d0',
          }}
          ref={crosswordProvider}
          data={data}
          onCorrect={onCorrectProvider}
          onLoadedCorrect={onLoadedCorrectProvider}
          onCrosswordCorrect={onCrosswordCorrectProvider}
          onCellChange={onCellChangeProvider}
          useStorage={false}
        >
          <CrosswordGrid />
          <div class="column">
            <DirectionClues direction="across" label="Horizontal" />
            <DirectionClues direction="down" label="Vertical" />
          </div>
        </CrosswordProvider>
      </CrosswordProviderWrapper>
      <CrosswordMessageBlock>
        <Messages ref={messagesProviderRef}>{messagesProvider}</Messages>
      </CrosswordMessageBlock>
    </Page>
  );
}

export default App;